import classNames from "classnames";
import { CSSProperties } from "react";

import { TableCell, TableCellProps } from "../parts/table_cell";
import { TableData } from "../table";
import { FillHandle } from "./fill_handle";
import interactiveCss from "./interactive_table.module.scss";
import { RangeEdges } from "./interactive_table.types";
import { useInteractiveTableStore } from "./interactive_table_store_context";
import {
  selectIsCellInFillArea,
  selectRangeEdges,
  selectShowFillHandle
} from "./interactive_table_store_selectors";
import { SelectionBorder } from "./selection_border";

const RESERVED_BACKGROUND_COLOR = "var(--palettePrimary3)";

/**
 * Converts edge flags into an inset box-shadow string that draws a border only
 * on the sides that sit on the outer boundary of the range. Inner edges get no
 * shadow so there are no internal grid lines inside a multi-cell selection.
 */
const edgesToBoxShadow = (edges: RangeEdges): CSSProperties["boxShadow"] =>
  [
    edges.top ? `inset 0 1.5px 0 0 var(--palettePrimary0)` : "",
    edges.bottom ? `inset 0 -1.5px 0 0 var(--palettePrimary0)` : "",
    edges.left ? `inset 1.5px 0 0 0 var(--palettePrimary0)` : "",
    edges.right ? `inset -1.5px 0 0 0 var(--palettePrimary0)` : ""
  ]
    .filter(Boolean)
    .join(", ");

type InteractiveTableCellProps<TABLE_DATA extends TableData> = TableCellProps<TABLE_DATA> & {
  isLastColumn?: boolean;
};

const InteractiveTableCellInner = <TABLE_DATA extends TableData>(
  props: InteractiveTableCellProps<TABLE_DATA>
) => {
  const { rowIndex, colIndex } = props;
  const coord = rowIndex !== undefined && colIndex !== undefined ? { rowIndex, colIndex } : null;

  const selectionEdges = useInteractiveTableStore((state) =>
    coord ? selectRangeEdges(state.selectionRange, coord) : null
  ) as RangeEdges | null;

  const fillEdges = useInteractiveTableStore((state) =>
    coord ? selectRangeEdges(state.fillRange, coord) : null
  ) as RangeEdges | null;

  const copiedEdges = useInteractiveTableStore((state) =>
    coord ? selectRangeEdges(state.copiedRange, coord) : null
  ) as RangeEdges | null;

  const showFillHandleFromStore = useInteractiveTableStore((state) =>
    selectShowFillHandle(state.selectionRange, state.fillRange, state.isFillDragging, coord)
  );

  const isCellInFillArea = useInteractiveTableStore((state) =>
    coord
      ? state.isFillDragging && selectIsCellInFillArea(state.selectionRange, state.fillRange, coord)
      : false
  );

  // Fill handle: single-cell selection, or tracks fill range corner during drag.
  // Suppressed when the row is disabled or the column reports the cell as read-only
  const isFillHandleDisabled =
    !!props.disabled || !!props.cell.column.columnDef.meta?.getIsCopyPasteBlocked?.(props.cell.row.original);
  const showFillHandle = !isFillHandleDisabled && !copiedEdges && showFillHandleFromStore;

  // Fill range border takes priority over selection border when both exist.
  const boxShadow = fillEdges
    ? edgesToBoxShadow(fillEdges)
    : selectionEdges
      ? edgesToBoxShadow(selectionEdges)
      : undefined;

  const backgroundColor = isCellInFillArea || copiedEdges ? RESERVED_BACKGROUND_COLOR : undefined;

  return (
    <TableCell
      {...props}
      className={classNames(props.className, interactiveCss.interactiveCell, {
        [interactiveCss.cellFillHighlight]: isCellInFillArea,
        [interactiveCss.cellCopied]: !!copiedEdges
      })}
      selectionBorder={boxShadow ? <SelectionBorder boxShadow={boxShadow} /> : undefined}
      additionalCellData={{ backgroundColor }}
      fillHandle={showFillHandle && coord ? <FillHandle coord={coord} /> : undefined}
    />
  );
};

/**
 * Drop-in replacement for TableCell when rendered inside an InteractiveTable.
 * Reads the interactive store, computes selection/fill/copy visual state, and
 * delegates final rendering to TableCell via the className/selectionBorder/
 * fillHandle passthrough props.
 */
export const InteractiveTableCell = <TABLE_DATA extends TableData>(
  props: InteractiveTableCellProps<TABLE_DATA>
) => {
  const { isLastColumn } = props;
  const nonInteractiveLastColumn = useInteractiveTableStore((state) => state.nonInteractiveLastColumn);

  if (nonInteractiveLastColumn && isLastColumn) {
    return <TableCell {...props} />;
  }

  return <InteractiveTableCellInner {...props} />;
};
